import { SFX_MANIFEST } from './audio-manifest';
import { t } from '../i18n/es';

/**
 * The credits of the game (GDD §3.3, §14.3, issue #136), generated from the data of the build so they cannot fall behind it:
 * every sound effect of the audio manifest with its author and licence (docs/CREDITS.md lists the same rows, and a test keeps
 * both in step), the fonts, the engine, the design system and the card that leaves the next case open. Pure data: the screen
 * renders it.
 */
export interface CreditEntry {
  name: string;
  /** What it is or who made it. */
  detail: string;
  licence: string;
}

export interface CreditSection {
  id: 'audio' | 'fonts' | 'engine' | 'design';
  title: string;
  entries: readonly CreditEntry[];
}

/** The parody names of the people of the story are never credited: this is the team and the tools only. */
export function creditSections(): readonly CreditSection[] {
  return [
    {
      id: 'audio',
      title: t('ui.cierre.credits.audio'),
      entries: SFX_MANIFEST.map((entry) => ({ name: entry.id, detail: entry.author, licence: entry.license })),
    },
    {
      id: 'fonts',
      title: t('ui.cierre.credits.fonts'),
      entries: [
        { name: 'Inter', detail: 'The Inter Project Authors', licence: 'OFL-1.1' },
        { name: 'JetBrains Mono', detail: 'JetBrains', licence: 'OFL-1.1' },
      ],
    },
    {
      id: 'engine',
      title: t('ui.cierre.credits.engine'),
      entries: [
        { name: 'Three.js', detail: 'mrdoob and the Three.js authors', licence: 'MIT' },
        { name: 'Vite', detail: 'Evan You and the Vite contributors', licence: 'MIT' },
      ],
    },
    {
      id: 'design',
      title: t('ui.cierre.credits.design'),
      entries: [{ name: 'Tactical Thriller', detail: 'Carlos Rojas', licence: 'MIT' }],
    },
  ];
}

/** The card after the credits: the next case is open, with no mechanic yet (GDD §3.3, #148). */
export const creditsTeaser = (): string => t('ui.cierre.credits.teaser');

/** Entries of the credits per page when the motion is reduced (#63): the credits are paged instead of scrolling. */
export const CREDITS_PAGE_SIZE = 8;

/** Every entry of every section in reading order, with its section, for the paged credits. */
export function creditEntries(): { section: CreditSection['id']; title: string; entry: CreditEntry }[] {
  return creditSections().flatMap((section) => section.entries.map((entry) => ({ section: section.id, title: section.title, entry })));
}
